// Environment variable helper functions

import { env, isDevelopment, isProduction, isStaging } from '@/config/env';
import type { EnvironmentConfig, RuntimeConfig } from '@/types/env';

/**
 * Get the complete environment configuration
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  return {
    api: {
      baseUrl: env.VITE_API_BASE_URL,
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
    },
    app: {
      name: env.VITE_APP_NAME,
      version: env.VITE_APP_VERSION,
      environment: env.VITE_APP_ENVIRONMENT,
      debug: isDevelopment(),
      analytics: {
        enabled: isProduction(),
        trackingId: undefined, // Add if needed
      },
      features: {
        enableAnalytics: true,
        enablePayouts: true,
        enableNotifications: !isDevelopment(),
      },
    },
  };
};

/**
 * Get runtime configuration
 */
export const getRuntimeConfig = (): RuntimeConfig => {
  return {
    isClient: typeof window !== 'undefined',
    isServer: typeof window === 'undefined',
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    isStaging: isStaging(),
  };
};

/**
 * Get environment-specific API base URL
 */
export const getApiBaseUrl = (): string => {
  return env.VITE_API_BASE_URL;
};

/**
 * Check if a feature is enabled
 */
export const isFeatureEnabled = (feature: string): boolean => {
  const config = getEnvironmentConfig();

  switch (feature) {
    case 'analytics':
      return config.app.features.enableAnalytics;
    case 'payouts':
      return config.app.features.enablePayouts;
    case 'notifications':
      return config.app.features.enableNotifications;
    default:
      return false;
  }
};

/**
 * Get debug mode status
 */
export const isDebugMode = (): boolean => {
  return isDevelopment();
};

/**
 * Log environment information (development only)
 */
export const logEnvironmentInfo = (): void => {
  if (!isDevelopment()) return;

  console.group('🌍 Environment Configuration');
  console.log('Environment:', env.VITE_APP_ENVIRONMENT);
  console.log('App Name:', env.VITE_APP_NAME);
  console.log('App Version:', env.VITE_APP_VERSION);
  console.log('API Base URL:', env.VITE_API_BASE_URL);
  console.groupEnd();
};
